import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../../services/api';
import { API_BASE_URL } from '../../config';
import { MapPin } from 'lucide-react';
import styles from './SuburbAddressAutocomplete.module.css';

const SuburbAddressAutocomplete = ({ value, onSelect, placeholder, disabled, onChange }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suburbResults, setSuburbResults] = useState([]);
    const [addressResults, setAddressResults] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        if (onChange) onChange(val);

        if (val.length < 3) {
            setSuburbResults([]);
            setAddressResults([]);
            setShowSuggestions(false);
            return;
        }

        // Debounce the API calls
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(val);
        }, 250);
    };

    const fetchSuggestions = async (query) => {
        // Call both APIs in parallel
        const [suburbRes, addressRes] = await Promise.allSettled([
            axios.get(`${API_BASE_URL}/api/SearchLocations/suggest?query=${encodeURIComponent(query)}`),
            api.get(`/api/GooglePlaces/addresscomplete?input=${encodeURIComponent(query)}`)
        ]);

        const suburbs = suburbRes.status === 'fulfilled' && suburbRes.value.data
            ? suburbRes.value.data.slice(0, 5)
            : [];

        const addresses = addressRes.status === 'fulfilled' && addressRes.value.predictions
            ? addressRes.value.predictions.slice(0, 5)
            : [];

        setSuburbResults(suburbs);
        setAddressResults(addresses);
        setShowSuggestions(suburbs.length > 0 || addresses.length > 0);
    };

    const handleSuburbSelect = (item) => {
        const displayString = item.displayName || `${item.suburb}, ${item.state} ${item.postcode}`;
        setInputValue(displayString);
        setShowSuggestions(false);

        if (onSelect) {
            onSelect({
                fullAddress: displayString,
                postcode: item.postcode,
                suburb: item.suburb || item.locality,
                state: item.state || item.mystate,
                source: 'suburb'
            });
        }
    };

    const handleAddressSelect = async (item) => {
        const address = item.description;
        setInputValue(address);
        setShowSuggestions(false);

        // Fetch place details to extract postcode/suburb/state
        try {
            const detailsResponse = await api.get(`/api/GooglePlaces/placedetails?placeId=${item.place_id}`);
            const result = detailsResponse.result;

            if (result) {
                let postcode = '';
                let suburb = '';
                let state = '';

                if (result.address_components) {
                    result.address_components.forEach(comp => {
                        if (comp.types.includes('postal_code')) postcode = comp.long_name;
                        if (comp.types.includes('locality')) suburb = comp.long_name;
                        if (comp.types.includes('administrative_area_level_1')) state = comp.short_name;
                    });
                }

                if (onSelect) {
                    onSelect({
                        fullAddress: address,
                        postcode,
                        suburb,
                        state,
                        placeId: item.place_id,
                        lat: result.geometry?.location?.lat,
                        lng: result.geometry?.location?.lng,
                        source: 'address'
                    });
                }
            } else if (onSelect) {
                onSelect({ fullAddress: address, placeId: item.place_id, source: 'address' });
            }
        } catch (error) {
            console.error("Error fetching place details:", error);
            if (onSelect) {
                onSelect({ fullAddress: address, placeId: item.place_id, source: 'address' });
            }
        }
    };

    const hasResults = suburbResults.length > 0 || addressResults.length > 0;

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.inputContainer}>
                <MapPin className={styles.inputIcon} size={16} />
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    disabled={disabled}
                    placeholder={placeholder || "Start typing suburb or address..."}
                    className={styles.input}
                    autoComplete="off"
                />
            </div>

            {showSuggestions && hasResults && (
                <ul className={styles.suggestionsList}>
                    {suburbResults.length > 0 && (
                        <>
                            <li className={styles.sectionHeader}>Suburbs</li>
                            {suburbResults.map((item, index) => (
                                <li
                                    key={`suburb-${index}`}
                                    onClick={() => handleSuburbSelect(item)}
                                    className={styles.suggestionItem}
                                >
                                    <MapPin size={14} style={{ marginRight: '8px', color: '#64748B', flexShrink: 0 }} />
                                    <span>{item.displayName || `${item.suburb}, ${item.state} ${item.postcode}`}</span>
                                </li>
                            ))}
                        </>
                    )}

                    {addressResults.length > 0 && (
                        <>
                            <li className={styles.sectionHeader}>Addresses</li>
                            {addressResults.map((item) => (
                                <li
                                    key={item.place_id}
                                    onClick={() => handleAddressSelect(item)}
                                    className={styles.suggestionItem}
                                >
                                    <MapPin size={14} style={{ marginRight: '8px', marginTop: '2px', color: '#64748B', flexShrink: 0 }} />
                                    <div className={styles.suggestionText}>
                                        <span className={styles.mainText}>
                                            {item.structured_formatting?.main_text || item.description}
                                        </span>
                                        {item.structured_formatting?.secondary_text && (
                                            <span className={styles.secondaryText}>
                                                {item.structured_formatting.secondary_text}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </>
                    )}
                </ul>
            )}
        </div>
    );
};

export default SuburbAddressAutocomplete;
